import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import MainLayout from "@/components/layout/MainLayout";
import { SuperAdminLayout } from "@/components/super-admin/SuperAdminLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Server,
  Edit2,
  Save,
  X,
  MessageSquare,
  BarChart3,
  Phone,
  Bot,
} from "lucide-react";

interface SystemPrompt {
  id: string;
  key: string;
  title: string;
  content: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const SystemSettingsPage = () => {
  const [editingPrompt, setEditingPrompt] = useState<SystemPrompt | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    description: "",
  });
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch system prompts
  const { data: systemPrompts, isLoading } = useQuery({
    queryKey: ["system-prompts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("system_prompts")
        .select("*")
        .order("key");

      if (error) {
        throw error;
      }

      return data as SystemPrompt[];
    },
  });

  // Update system prompt mutation
  const updatePromptMutation = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<SystemPrompt>;
    }) => {
      const { data, error } = await supabase
        .from("system_prompts")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["system-prompts"] });
      toast({
        title: "Success",
        description: "System prompt updated successfully",
      });
      setIsDialogOpen(false);
      setEditingPrompt(null);
    },
    onError: (error) => {
      console.error("Update error:", error);
      toast({
        title: "Error",
        description: "Failed to update system prompt",
        variant: "destructive",
      });
    },
  });

  const handleEditPrompt = (prompt: SystemPrompt) => {
    setEditingPrompt(prompt);
    setFormData({
      title: prompt.title,
      content: prompt.content,
      description: prompt.description || "",
    });
    setIsDialogOpen(true);
  };

  const handleSavePrompt = () => {
    if (!editingPrompt) return;

    updatePromptMutation.mutate({
      id: editingPrompt.id,
      updates: {
        title: formData.title,
        content: formData.content,
        description: formData.description || null,
        updated_at: new Date().toISOString(),
      },
    });
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingPrompt(null);
    setFormData({ title: "", content: "", description: "" });
  };

  // Categorize system prompts
  const mainConversationPrompts =
    systemPrompts?.filter((prompt) =>
      [
        "default_system_prompt",
        "form_field_instructions",
        "formatting_instructions",
        "conversation_closing_instructions",
        "knowledge_base_instructions",
      ].includes(prompt.key),
    ) || [];

  const voiceConversationPrompts =
    systemPrompts?.filter((prompt) =>
      ["default_system_prompt_voice"].includes(prompt.key),
    ) || [];

  const postChatAnalysisPrompts =
    systemPrompts?.filter((prompt) =>
      [
        "form_field_extraction_instructions",
        "conversation_analysis_instructions",
      ].includes(prompt.key),
    ) || [];

  const aiAssistantPrompts =
    systemPrompts?.filter((prompt) =>
      [
        "ai_assistant_master_prompt",
        "ai_assistant_chat_master_prompt",
      ].includes(prompt.key),
    ) || [];

  const renderPromptCard = (prompt: SystemPrompt) => (
    <Card key={prompt.id} className="border-l-4 border-l-primary">
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-semibold text-lg">{prompt.title}</h3>
              <span className="bg-muted px-2 py-1 rounded text-xs font-mono">
                {prompt.key}
              </span>
              {!prompt.is_active && (
                <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs">
                  Inactive
                </span>
              )}
            </div>
            {prompt.description && (
              <p className="text-sm text-muted-foreground mb-3">
                {prompt.description}
              </p>
            )}
            <div className="bg-muted/30 p-3 rounded border">
              <p className="text-sm font-mono whitespace-pre-wrap line-clamp-3">
                {prompt.content}
              </p>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Last updated: {new Date(prompt.updated_at).toLocaleString()}
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleEditPrompt(prompt)}
            className="ml-4"
          >
            <Edit2 className="h-4 w-4 mr-2" />
            Edit
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <MainLayout>
      <SuperAdminLayout
        title="System Settings"
        description="Configure system-wide settings and parameters"
      >
        <div className="space-y-6">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
            </div>
          ) : (
            <>
              {/* Main AI Conversation Section */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <MessageSquare className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle>Main AI Conversation</CardTitle>
                      <CardDescription>
                        System prompts that control AI agent behavior during
                        live conversations (chat-with-agent function)
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {mainConversationPrompts.length > 0 ? (
                    <div className="space-y-4">
                      {mainConversationPrompts.map(renderPromptCard)}
                    </div>
                  ) : (
                    <p className="text-center py-8 text-muted-foreground">
                      No main conversation prompts found.
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Voice Agent Conversation Section */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Phone className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <CardTitle>Voice Agent Conversation</CardTitle>
                      <CardDescription>
                        System prompts specifically for voice agents during
                        phone calls (voice-agent-service function)
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {voiceConversationPrompts.length > 0 ? (
                    <div className="space-y-4">
                      {voiceConversationPrompts.map(renderPromptCard)}
                    </div>
                  ) : (
                    <p className="text-center py-8 text-muted-foreground">
                      No voice conversation prompts found.
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* AI Assistant Section */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                      <Bot className="h-6 w-6 text-yellow-600" />
                    </div>
                    <div>
                      <CardTitle>AI Assistants</CardTitle>
                      <CardDescription>
                        System prompts that control AI assistant behavior for
                        content generation (generate-with-assistant function)
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {aiAssistantPrompts.length > 0 ? (
                    <div className="space-y-4">
                      {aiAssistantPrompts.map(renderPromptCard)}
                    </div>
                  ) : (
                    <p className="text-center py-8 text-muted-foreground">
                      No AI assistant prompts found.
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Post-Chat Analysis Section */}
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <BarChart3 className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <CardTitle>Post-Chat Analysis</CardTitle>
                      <CardDescription>
                        System prompts for analyzing conversations and
                        extracting insights after chat completion
                        (extract-form-fields function)
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {postChatAnalysisPrompts.length > 0 ? (
                    <div className="space-y-4">
                      {postChatAnalysisPrompts.map(renderPromptCard)}
                    </div>
                  ) : (
                    <p className="text-center py-8 text-muted-foreground">
                      No post-chat analysis prompts found.
                    </p>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Edit Prompt Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit System Prompt</DialogTitle>
              <DialogDescription>
                Modify the system prompt content. Changes will affect all AI
                agents immediately.
              </DialogDescription>
            </DialogHeader>

            {editingPrompt && (
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        title: e.target.value,
                      }))
                    }
                    placeholder="Enter prompt title"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    placeholder="Enter prompt description (optional)"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="content">Content</Label>
                  <Textarea
                    id="content"
                    value={formData.content}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        content: e.target.value,
                      }))
                    }
                    placeholder="Enter prompt content"
                    className="min-h-[300px] font-mono text-sm"
                  />
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                  <p className="text-sm text-yellow-800">
                    <strong>Warning:</strong> Changes to system prompts will
                    immediately affect all AI agents across the platform. Please
                    test thoroughly before saving.
                  </p>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button variant="outline" onClick={handleCloseDialog}>
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSavePrompt}
                    disabled={updatePromptMutation.isPending}
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {updatePromptMutation.isPending
                      ? "Saving..."
                      : "Save Changes"}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </SuperAdminLayout>
    </MainLayout>
  );
};

export default SystemSettingsPage;
